import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Shield } from "lucide-react";
import { Link } from "@tanstack/react-router";

const ROLE_LABELS = {
  COMMUNITY_ADMIN: "Administrador del barrio",
  RESIDENT: "Residente",
  SECURITY: "Seguridad",
  MAINTENANCE: "Mantenimiento",
  AMENITY_STAFF: "Personal de amenities",
} as const;

export function AdminUsersPage() {
  const utils = trpc.useUtils();
  const { data: users, isLoading } = trpc.admin.getAllUsers.useQuery();
  const updateUserPlan = trpc.admin.updateUserPlan.useMutation({
    onSuccess: () => {
      utils.admin.getAllUsers.invalidate();
    },
  });

  const handlePlanChange = (userId: string, plan: "BETA" | "PRO" | "NONE") => {
    updateUserPlan.mutate({
      userId,
      plan: plan === "NONE" ? null : plan,
    });
  };

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat("es-AR", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  const getRoleLabel = (role: string) => {
    return ROLE_LABELS[role as keyof typeof ROLE_LABELS] || role;
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold md:text-3xl">Panel de Admin</h1>
        <p className="text-muted-foreground">Gestioná usuarios y barrios.</p>
      </div>

      <div className="mb-6 flex gap-2">
        <Link
          to="/admin/users"
          className="rounded-xl px-4 py-2 text-sm font-medium bg-primary text-primary-foreground"
        >
          Usuarios
        </Link>
        <Link
          to="/admin/communities"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Barrios
        </Link>
        <Link
          to="/admin/communications"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Comunicados
        </Link>
        <Link
          to="/admin/my-access"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Mi acceso
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gestión de usuarios</CardTitle>
          <CardDescription>
            Consultá y gestioná los planes de acceso de los usuarios.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users?.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between rounded-xl border p-4"
              >
                <div className="flex items-center gap-4 flex-1">
                  <Avatar>
                    <AvatarImage
                      src={user.image || undefined}
                      alt={user.name || "Usuario"}
                    />
                    <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">
                        {user.name || "Sin nombre"}
                      </p>
                      {user.isSuperAdmin && (
                        <Badge variant="secondary" className="gap-1">
                          <Shield className="h-3 w-3" />
                          Superadmin
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {user.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Registrado {formatDate(user.createdAt)}
                    </p>
                    {user.communityMemberships.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {user.communityMemberships.map((m) => (
                          <Badge
                            key={m.id}
                            variant="outline"
                            className="text-xs"
                          >
                            {m.community.name} ({getRoleLabel(m.role)})
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right min-w-[120px]">
                    <p className="text-xs text-muted-foreground mb-1">Plan</p>
                    <Select
                      value={user.plan || "NONE"}
                      onValueChange={(value) =>
                        handlePlanChange(
                          user.id,
                          value as "BETA" | "PRO" | "NONE",
                        )
                      }
                      disabled={updateUserPlan.isPending}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Sin plan</SelectItem>
                        <SelectItem value="BETA">Beta</SelectItem>
                        <SelectItem value="PRO">Pro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
